/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { getAuth, getIdToken } from 'firebase/auth';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingIndicator, toast } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useHistory } from '../../navigation';
import { apiQuery, startSAMLLogin } from '../../state';

/**
 * Lists SSO providers available for login.
 */
export function SSOProviderLoginList(props: {
    organization: api.Organization;
    className?: string;
}): React.ReactElement {
    const { organization, className } = props;
    const history = useHistory();
    const [loading, _, setLoading] = useLoadingState();

    const { data: ssoProviders } = useSuspenseQuery(
        apiQuery('listSSOProviderLoginsInOrganization', { organizationId: organization.id })
    );
    const searchParams = new URLSearchParams(history.location.search);
    const requestedProvider = searchParams.get('saml_provider');

    const loginWith = async (provider: api.OrganizationSSOProviderLogin) => {
        setLoading(true);

        analytics.logEvent(
            analytics.createOrgEvent(organization.id, {
                type: 'auth_sso_sign_in',
            })
        );

        const url = new URL(provider.startURL);

        const returnTo = searchParams.get('saml_return_to') || history.location.pathname;
        if (returnTo) {
            url.searchParams.append('location', returnTo);
        }

        try {
            const user = getAuth().currentUser;
            const idToken = user ? await getIdToken(user) : null;
            await startSAMLLogin(url, idToken);
        } catch (error) {
            // On success the page navigates away, so only a failure ends the loading state.
            setLoading(false);
            toast.error('Error while signing in with SSO', error instanceof Error ? error : null);
        }
    };

    const mappedProviders = ssoProviders?.items
        .filter((provider) => !requestedProvider || provider.id === requestedProvider)
        .map((provider) => (
            <Button
                leadingIcon={loading ? LoadingIndicator : Icon.SsoSaml}
                kind="primary"
                size="large"
                testID="login.ssoButton"
                disabled={loading}
                onPress={() => loginWith(provider)}
                label={`Sign in with ${provider.label}`}
                key={provider.id}
            />
        ));

    return <div className={clsx('flex flex-col gap-4', className)}>{mappedProviders}</div>;
}
